import { useMemo, useState } from "react";
import {
  List,
  TextField,
  WrapperField,
  DatagridConfigurable,
  WithRecord,
  DateField,
  FunctionField,
  NumberField,
  BooleanField,
  BulkDeleteWithConfirmButton,
  useResourceContext,
  useTheme,
  usePermissions,
  RecordContextProvider,
} from "react-admin";
import SaleAddDialog from "../../components/sales/create/SaleAddDialog";
import SaleEditDialog from "../../components/sales/edit/SaleEditDialog";
import saleFilterList from "../../components/sales/filter/saleFilterList";
import TableListActions from "../../components/tableListActions/TableListActions";
import SaleExpandList from "../../components/sales/expand/SaleExpandList";
import ConfirmBtn from "../../components/confirmBtn/ConfirmBtn";
import EditBtn from "../../components/editBtn/EditBtn";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import PrintBtn from "../../components/printBtn/PrintBtn";
import PrintHandler from "../../components/printHandler/PrintHandler";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import DownloadAction from "../../components/tableListActions/DownloadActionNew";
import PrintContent from "../../components/sales/print/PrintContent";
import PrintInvoiceContent from "../../components/sales/print/invoice/PrintInvoiceContent";
import PrintBtnInvoice from "../../components/printBtnInvoice/PrintBtnInvoice";
import MyDatagridBody from "../../components/common/MyDatagridBody";
import SaleFooterList from "../../components/sales/footer/SaleFooterList";
import ResetFilterButton from "../../components/tableListActions/ResetFilterButton";
import ManualReportList from "../../components/reports/ManualReportList";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const SaleList = (props) => {
  const resource = useResourceContext();
  const { permissions } = usePermissions();
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [recordId, setRecordId] = useState();
  const [resetPrintContent, setResetPrintContent] = useState();
  const [printRecord, setPrintRecord] = useState({}); // initial value wajib object kosong untuk cek key id di dependency useeffect DeliveryNote.js
  const [resetPrintInvoiceContent, setResetPrintInvoiceContent] = useState();
  const [printInvoiceRecord, setPrintInvoiceRecord] = useState({}); // initial value wajib object kosong untuk cek key id di dependency useeffect DeliveryNote.js
  const [reportApplied, setReportApplied] = useState(false);
  const [theme] = useTheme("light");
  const forReport = resource === "sale-cards";
  const defaultFilterValues = useMemo(() => {
    const defaults = {
      start_date: firstDate,
      end_date: lastDate,
    };

    if (forReport) {
      defaults.approved = 1;
    }

    return defaults;
  }, [forReport]);

  const {
    currentCompanyFilter,
    filterDefaultValues,
    locationFilter,
  } = useCompanyScopedListFilters(defaultFilterValues);

  const parentFilter = useMemo(() => {
    const nextFilter = {
      ...locationFilter,
      ...(currentCompanyFilter.company_id
        ? { company_id: currentCompanyFilter.company_id }
        : {}),
      start_date: locationFilter.start_date || firstDate,
      end_date: locationFilter.end_date || lastDate,
    };

    if (forReport) {
      nextFilter.approved = 1;
    }

    return nextFilter;
  }, [currentCompanyFilter.company_id, locationFilter, forReport]);

  const handleClickOpenAdd = () => {
    setOpenAdd(true);
  };
  const handleClickOpenEdit = (recId) => (e) => {
    e.stopPropagation();
    setRecordId(recId);
    setOpenEdit(true);
  };
  const rowClickHandler = () => {};

  const list = useMemo(() => {
    let actions = (
          <TableListActions
        createHandler={handleClickOpenAdd}
        filter={false}
        exporter={false}
        createPermission="create_sale"
        extraActions={<ResetFilterButton defaultFilters={filterDefaultValues} />}
      />
    );

    let bulkActionButtons = <BulkDeleteWithConfirmButton />;

    let boolAct = [
      <BooleanField
        key={1}
        source="approved"
        label="mine.label.approved"
        textAlign="center"
      />,
      <WrapperField key={2} label="mine.label.actions" textAlign="center">
        <WithRecord
          render={(record) => {
            if (!record.approved && record.code.endsWith("P")) {
              return "-";
            }
            return !record.approved ? (
              !record.promotion_id ? (
                <>
                  <EditBtn
                    editHandler={handleClickOpenEdit(record.id)}
                    // label=""
                    variant="outlined"
                    sx={{
                      width: "fit-content",
                    }}
                  />
                  <ConfirmBtn
                    title={"mine.message.approve"}
                    content="mine.message.approve_content"
                    label="mine.btn.approve"
                    resource={resource + "/approve"}
                    startIcon={<DoneIcon />}
                    variant="outlined"
                    invalidateQueries={["sale-orders"]}
                    sx={{
                      mx: 1,
                    }}
                  />
                </>
              ) : null
            ) : (
              <>
                <PrintBtn
                  recordId={record.id}
                  setPrintRecord={setPrintRecord}
                  setResetPrintContent={setResetPrintContent}
                  btnProps={{
                    sx: {
                      mx: 0.5,
                    },
                  }}
                />
                <PrintBtnInvoice
                  recordId={record.id}
                  setPrintRecord={setPrintInvoiceRecord}
                  setResetPrintContent={setResetPrintInvoiceContent}
                  btnProps={{
                    label: "Invoice",
                    sx: {
                      mx: 0.5,
                    },
                  }}
                  plateNumber={record.transport.plate}
                />
                {permissions.administrator ? (
                  <ConfirmBtn
                    title="mine.message.unapprove"
                    content="mine.message.unapprove_content"
                    label="mine.label.unapprove"
                    resource={resource + "/unapprove"}
                    startIcon={<CloseIcon />}
                    color="error"
                    sx={{
                      mx: 0.5,
                    }}
                  />
                ) : null}
              </>
            );
          }}
        />
      </WrapperField>,
    ];

    if (forReport) {
      actions = <DownloadAction />;
      bulkActionButtons = false;
      boolAct = null;
    }

    const ReportList = forReport ? ManualReportList : List;
    return (
      <ReportList
        title={props.title ? props.title : undefined}
        storeKey={false}
        empty={false}
        // aside={showAside || forReport ? <PurchaseAsideList /> : null}
        actions={actions}
        filters={saleFilterList(permissions)}
        filter={forReport ? undefined : parentFilter}
        filterDefaultValues={filterDefaultValues}
        requiredFilters={forReport ? ["start_date", "end_date"] : undefined}
        onApplied={forReport ? () => setReportApplied(true) : undefined}
        onReset={forReport ? () => setReportApplied(false) : undefined}
        queryOptions={{ meta: { sort: ["code DESC"] } }} // overide default sort
        sx={{
          flexDirection: "column",
        }}
        >
          <DatagridConfigurable
          body={
            <MyDatagridBody
              footer={
                <SaleFooterList forReport={forReport} filter={forReport ? {} : parentFilter} enabled={!forReport || reportApplied} />
              }
            />
          }
            bulkActionButtons={bulkActionButtons}
            rowClick={rowClickHandler}
          rowSx={(record, index) => {
            // redupkan text SJ gratis
            if (record.code.endsWith("P")) {
              return {
                "& .RaDatagrid-rowCell": {
                  color: theme !== "dark" ? "grey.500" : "rgba(255,255,255,.4)",
                },
              };
            }
          }}
          expand={<SaleExpandList forReport={forReport} />}
          sx={{
            "& .RaDatagrid-expandedPanel > td": {
              p: 0,
              bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
            },
          }}
        >
          <TextField label="mine.label.depo" source="company.alias" />
          <TextField source="code" label="mine.label.receipt_no" />
          <FunctionField
            label="mine.label.order_no"
            render={(record) => {
              if (!record?.sale_items?.length) return "-";

              const order = record.sale_items[0]?.sale_order_item?.sale_order;
              return order?.code || "-";
            }}
          />
          <DateField
            source="date"
            label="mine.label.date"
            options={{
              year: "numeric",
              month: "short",
              day: "2-digit",
            }}
          />
          <FunctionField
            label="mine.label.customer"
            render={(record) => {
              const saleOrder =
                record?.sale_items?.[0]?.sale_order_item?.sale_order;
              return saleOrder?.customer?.name || "-";
            }}
          />
          <FunctionField
            label="Salesman"
            render={(record) => {
              const saleOrder =
                record?.sale_items?.[0]?.sale_order_item?.sale_order;
              return saleOrder?.salesman?.name || "-";
            }}
          />
          <FunctionField
            label="Transport"
            render={(record) => `${record.transport.code}`}
          />
          <TextField
            source="promotion.name"
            label="mine.label.promotion"
            emptyText="-"
          />
          <FunctionField
            textAlign="right"
            label="Box"
            render={(record) => {
              let rec = { total_sent: 0 };
              for (let i = 0; i < record.sale_items.length; i++) {
                rec.total_sent += +record.sale_items[i].sent_quantity;
              }
              return (
                <RecordContextProvider value={rec}>
                  <NumberField source="total_sent" />
                </RecordContextProvider>
              );
            }}
          />
          <NumberField source="total" label="mine.label.value" />
          <TextField source="note" label="mine.label.note" emptyText="-" />
          {boolAct}
        </DatagridConfigurable>
      </ReportList>
    );
  }, [
    forReport,
    filterDefaultValues,
    parentFilter,
    permissions,
    props.title,
    theme,
    resource,
    reportApplied,
  ]);
  return (
    <>
      {list}
      {!forReport && (
        <>
          <SaleAddDialog open={openAdd} setOpen={setOpenAdd} />
          <SaleEditDialog
            open={openEdit}
            setOpen={setOpenEdit}
            recordId={recordId}
          />
          <PrintHandler
            record={printRecord}
            resetPrintContent={resetPrintContent}
            Content={PrintContent}
          />
          <PrintHandler
            record={printInvoiceRecord}
            resetPrintContent={resetPrintInvoiceContent}
            Content={PrintInvoiceContent}
          />
        </>
      )}
    </>
  );
};

export default SaleList;
